import { useState, useEffect, useCallback, useRef } from 'react';
import { CheckoutSection } from './CheckoutSection';
import { BillingAddressFormData, CreditCardFormData, ShippingLocation } from '../../types';
import { PaymentService } from '../../services/paymentService';
import { useAddressAutocomplete } from '../../hooks/useAddressAutocomplete';

const BILLING_COUNTRIES = ['us'];

// BluePay.js type declarations
declare global {
  interface Window {
    BluePay: {
      setCredentials: (accountId: string, hashedKey: string) => void;
      createToken: (formId: string, callback: BluePayCallback) => void;
    };
  }
}

type BluePayCallback = (response: BluePayResponse, error?: string) => void;

interface BluePayResponse {
  STATUS: string;
  MESSAGE: string;
  PAYMENT_ACCOUNT?: string;
  CARD_EXPIRE?: string;
  TOKEN?: string;
  TRANS_ID?: string;
}

interface CreditCardSectionProps {
  billingAddress: BillingAddressFormData;
  creditCard: CreditCardFormData;
  shippingAddress?: Partial<ShippingLocation>;
  onBillingAddressChange: (address: BillingAddressFormData) => void;
  onCreditCardChange: (card: CreditCardFormData) => void;
  onTokenReceived: (token: string) => void;
  onError: (error: string) => void;
  totalAmount: number;
}

/**
 * Credit card payment section for checkout.
 * Includes billing address and card details with BluePay.js tokenization.
 * Fetches payment processor credentials from the API on mount.
 */
export function CreditCardSection({
  billingAddress,
  creditCard,
  shippingAddress,
  onBillingAddressChange,
  onCreditCardChange,
  onTokenReceived,
  onError,
  totalAmount,
}: CreditCardSectionProps) {
  const [isBluePayLoaded, setIsBluePayLoaded] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [cardNumber, setCardNumber] = useState('');
  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const isAmex = (digits: string) => /^3[47]/.test(digits);

  const formatCardNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (isAmex(digits)) {
      const limited = digits.slice(0, 15);
      if (limited.length <= 4) return limited;
      if (limited.length <= 10) return `${limited.slice(0, 4)}-${limited.slice(4)}`;
      return `${limited.slice(0, 4)}-${limited.slice(4, 10)}-${limited.slice(10)}`;
    }
    const limited = digits.slice(0, 16);
    if (limited.length <= 4) return limited;
    if (limited.length <= 8) return `${limited.slice(0, 4)}-${limited.slice(4)}`;
    if (limited.length <= 12) return `${limited.slice(0, 4)}-${limited.slice(4, 8)}-${limited.slice(8)}`;
    return `${limited.slice(0, 4)}-${limited.slice(4, 8)}-${limited.slice(8, 12)}-${limited.slice(12)}`;
  };

  // Fetch payment config and load BluePay.js script
  useEffect(() => {
    let isMounted = true;

    const initializePayment = async () => {
      try {
        // Fetch credentials from API
        const config = await PaymentService.getPaymentConfig();

        if (!isMounted) return;

        setIsLoadingConfig(false);

        // Load BluePay.js script if not already loaded
        if (window.BluePay) {
          window.BluePay.setCredentials(config.accountId, config.hashedKey);
          setIsBluePayLoaded(true);
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://secure.bluepay.com/v3/bluepay.js';
        script.async = true;
        script.onload = () => {
          if (!isMounted) return;
          if (window.BluePay) {
            window.BluePay.setCredentials(config.accountId, config.hashedKey);
            setIsBluePayLoaded(true);
          }
        };
        script.onerror = () => {
          if (!isMounted) return;
          setConfigError('Failed to load payment processor. Please refresh and try again.');
          onErrorRef.current('Failed to load payment processor. Please refresh and try again.');
        };
        document.body.appendChild(script);
      } catch {
        if (!isMounted) return;
        setIsLoadingConfig(false);
        const message = 'Unable to initialize payment. Please try again later.';
        setConfigError(message);
        onErrorRef.current(message);
      }
    };

    initializePayment();

    return () => {
      isMounted = false;
    };
  }, []);

  const isInternationalShipping = shippingAddress?.countrycode === 'ZZ';

  // International billing must always be a US address — reset sameAsShipping when switching to international
  useEffect(() => {
    if (isInternationalShipping && billingAddress.sameAsShipping) {
      onBillingAddressChange({ ...billingAddress, sameAsShipping: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to international flag change
  }, [isInternationalShipping]);

  // Copy shipping address when "Same as Shipping" is checked (never for international)
  useEffect(() => {
    if (!isInternationalShipping && billingAddress.sameAsShipping && shippingAddress) {
      onBillingAddressChange({
        ...billingAddress,
        streetline1: shippingAddress.address1 || '',
        streetline2: shippingAddress.address2 || '',
        city: shippingAddress.city || '',
        state: shippingAddress.state || '',
        postalcode: shippingAddress.zip || '',
        phone: shippingAddress.phone || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to sameAsShipping toggle and shippingAddress changes; including billingAddress/onBillingAddressChange would cause infinite loops
  }, [billingAddress.sameAsShipping, shippingAddress]);

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const hasError = (field: string, value: string) => {
    return touched[field] && !value;
  };

  const inputClass = (field: string, value: string, disabled = false) => {
    const base = 'w-full rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent';
    if (disabled) {
      return `${base} border border-gray-200 bg-gray-100 text-gray-500`;
    }
    if (hasError(field, value)) {
      return `${base} border-2 border-red-500 bg-red-50`;
    }
    return `${base} border border-gray-300`;
  };

  // Tokenize card data via BluePay
  const tokenizeCard = useCallback(() => {
    // Testing escape hatch: set window.__BLUEPAY_MOCK__ = 'approved' | 'declined' in the browser console
    if (import.meta.env.VITE_DEV_MODE === 'true') {
      const mock = (window as unknown as Record<string, unknown>).__BLUEPAY_MOCK__;
      if (mock === 'declined') {
        onError('Card declined (mock)');
        return;
      }
      if (mock === 'approved') {
        onTokenReceived('mock_tok_' + Date.now());
        return;
      }
    }

    if (!isBluePayLoaded || !window.BluePay) {
      onError('Payment processor not loaded. Please refresh and try again.');
      return;
    }

    window.BluePay.createToken('credit-card-form', (response, error) => {

      if (error) {
        onError('Network error contacting payment provider. Please try again.');
        return;
      }

      if (response.STATUS !== '1') {
        onError(response.MESSAGE || 'Card validation failed. Please check your card details.');
        return;
      }

      const token = response.TOKEN || response.TRANS_ID;
      if (token) {
        onTokenReceived(token);
      } else {
        onError('No payment token received. Please try again.');
      }
    });
  }, [isBluePayLoaded, onError, onTokenReceived]);

  // Expose tokenize function via window for form submission
  useEffect(() => {
    (window as unknown as Record<string, unknown>).tokenizeCreditCard = tokenizeCard;
    return () => {
      delete (window as unknown as Record<string, unknown>).tokenizeCreditCard;
    };
  }, [tokenizeCard]);

  const isAddressDisabled = billingAddress.sameAsShipping;

  const [streetline1El, setStreetline1El] = useState<HTMLInputElement | null>(null);
  const streetline1Ref = useCallback((node: HTMLInputElement | null) => setStreetline1El(node), []);
  useAddressAutocomplete(
    streetline1El,
    (parsed) => {
      onBillingAddressChange({ ...billingAddress, streetline1: parsed.address1, city: parsed.city, state: parsed.state, postalcode: parsed.zip });
    },
    { countries: BILLING_COUNTRIES, disabled: isAddressDisabled }
  );

  // Show loading state while fetching config
  if (isLoadingConfig) {
    return (
      <CheckoutSection title="Payment">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading payment options...</span>
        </div>
      </CheckoutSection>
    );
  }

  // Show error state if config failed to load
  if (configError) {
    return (
      <CheckoutSection title="Payment">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-red-800">{configError}</span>
          </div>
        </div>
      </CheckoutSection>
    );
  }

  return (
    <>
      {/* Billing Address */}
      <CheckoutSection title="Billing Address">
        {isInternationalShipping && (
          <p className="mb-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
            Billing address must be US-based.
          </p>
        )}
        {shippingAddress && !isInternationalShipping && (
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={billingAddress.sameAsShipping}
                onChange={(e) =>
                  onBillingAddressChange({
                    ...billingAddress,
                    sameAsShipping: e.target.checked,
                  })
                }
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Same as Shipping Address</span>
            </label>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="billing_streetline1" className="block text-sm font-medium text-gray-700 mb-1">
              Address <span className="text-red-500">*</span>
            </label>
            <input
              ref={streetline1Ref}
              id="billing_streetline1"
              type="text"
              required
              disabled={isAddressDisabled}
              className={inputClass('streetline1', billingAddress.streetline1, isAddressDisabled)}
              value={billingAddress.streetline1}
              onChange={(e) =>
                onBillingAddressChange({ ...billingAddress, streetline1: e.target.value })
              }
              onBlur={() => handleBlur('streetline1')}
              placeholder="Address Line 1"
            />
            <input
              id="billing_streetline2"
              type="text"
              disabled={isAddressDisabled}
              className={`mt-2 ${inputClass('streetline2', '', isAddressDisabled)}`}
              value={billingAddress.streetline2}
              onChange={(e) =>
                onBillingAddressChange({ ...billingAddress, streetline2: e.target.value })
              }
              placeholder="Address Line 2"
            />
          </div>
          <div>
            <label htmlFor="billing_city" className="block text-sm font-medium text-gray-700 mb-1">
              City <span className="text-red-500">*</span>
            </label>
            <input
              id="billing_city"
              type="text"
              required
              disabled={isAddressDisabled}
              className={inputClass('billing_city', billingAddress.city, isAddressDisabled)}
              value={billingAddress.city}
              onChange={(e) =>
                onBillingAddressChange({ ...billingAddress, city: e.target.value })
              }
              onBlur={() => handleBlur('billing_city')}
            />
          </div>
          <div>
            <label htmlFor="billing_state" className="block text-sm font-medium text-gray-700 mb-1">
              State <span className="text-red-500">*</span>
            </label>
            <input
              id="billing_state"
              type="text"
              required
              disabled={isAddressDisabled}
              className={inputClass('billing_state', billingAddress.state, isAddressDisabled)}
              value={billingAddress.state}
              onChange={(e) =>
                onBillingAddressChange({ ...billingAddress, state: e.target.value })
              }
              onBlur={() => handleBlur('billing_state')}
            />
          </div>
          <div>
            <label htmlFor="billing_postalcode" className="block text-sm font-medium text-gray-700 mb-1">
              Postal Code <span className="text-red-500">*</span>
            </label>
            <input
              id="billing_postalcode"
              type="text"
              required
              disabled={isAddressDisabled}
              className={inputClass('billing_postalcode', billingAddress.postalcode, isAddressDisabled)}
              value={billingAddress.postalcode}
              onChange={(e) =>
                onBillingAddressChange({ ...billingAddress, postalcode: e.target.value })
              }
              onBlur={() => handleBlur('billing_postalcode')}
            />
          </div>
          <div>
            <label htmlFor="billing_phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              id="billing_phone"
              type="tel"
              disabled={isAddressDisabled}
              className={inputClass('billing_phone', '', isAddressDisabled)}
              value={billingAddress.phone}
              onChange={(e) =>
                onBillingAddressChange({ ...billingAddress, phone: e.target.value })
              }
            />
          </div>
        </div>
      </CheckoutSection>

      {/* Card Payment */}
      <CheckoutSection title="Card Payment">
        <form id="credit-card-form" onSubmit={(e) => e.preventDefault()}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="card_firstname" className="block text-sm font-medium text-gray-700 mb-1">
                Cardholder First Name <span className="text-red-500">*</span>
              </label>
              <input
                id="card_firstname"
                type="text"
                required
                className={inputClass('card_firstname', creditCard.cardholderFirstName)}
                value={creditCard.cardholderFirstName}
                onChange={(e) =>
                  onCreditCardChange({ ...creditCard, cardholderFirstName: e.target.value })
                }
                onBlur={() => handleBlur('card_firstname')}
              />
            </div>
            <div>
              <label htmlFor="card_lastname" className="block text-sm font-medium text-gray-700 mb-1">
                Cardholder Last Name <span className="text-red-500">*</span>
              </label>
              <input
                id="card_lastname"
                type="text"
                required
                className={inputClass('card_lastname', creditCard.cardholderLastName)}
                value={creditCard.cardholderLastName}
                onChange={(e) =>
                  onCreditCardChange({ ...creditCard, cardholderLastName: e.target.value })
                }
                onBlur={() => handleBlur('card_lastname')}
              />
            </div>
            <div>
              <label htmlFor="card_account_display" className="block text-sm font-medium text-gray-700 mb-1">
                Card Number <span className="text-red-500">*</span>
              </label>
              <input
                type="hidden"
                id="card_account"
                data-encrypted-name="CARD_ACCOUNT"
                value={cardNumber}
              />
              <input
                id="card_account_display"
                type="text"
                inputMode="numeric"
                autoComplete="cc-number"
                required
                maxLength={isAmex(cardNumber) ? 17 : 19}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formatCardNumber(cardNumber)}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  setCardNumber(digits.slice(0, isAmex(digits) ? 15 : 16));
                }}
                placeholder="1234-5678-9012-3456"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="card_expmo" className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    id="card_expmo"
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-exp-month"
                    required
                    data-encrypted-name="EXPMO"
                    maxLength={2}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="MM"
                  />
                  <input
                    id="card_expyr"
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-exp-year"
                    required
                    data-encrypted-name="EXPYR"
                    maxLength={2}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="YY"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="card_cvv2" className="block text-sm font-medium text-gray-700 mb-1">
                  CVV <span className="text-red-500">*</span>
                </label>
                <input
                  id="card_cvv2"
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  required
                  data-encrypted-name="CARD_CVV2"
                  maxLength={4}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="123"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Charges:</span>
              <span className="text-lg font-semibold text-gray-900">
                ${totalAmount.toFixed(2)}
              </span>
            </div>
          </div>

          {!isBluePayLoaded && (
            <div className="mt-4 text-sm text-gray-500 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Loading secure payment form...
            </div>
          )}
        </form>
      </CheckoutSection>
    </>
  );
}
